import { ZodType } from 'zod';

import { logger } from '~/logger';

import { DEBUG_LEVEL, logApiRequest } from './logging';

/**
 * API error codes constants
 * APIエラーコードの定数
 */
export const API_ERROR_CODES = {
  SESSION_INVALID: '4000',
} as const;

/**
 * API response with headers and body
 * ヘッダーとボディを含むAPIレスポンス
 */
export interface ApiResponse<T = unknown> {
  headers: Headers;
  body: T;
}

/**
 * Fetch function that returns full response (headers and body)
 * 完全なレスポンス（ヘッダーとボディ）を返すfetch関数
 *
 * @description
 * Extended version of fetchApi that returns both response headers and body.
 * Useful when you need access to response headers (e.g., for pagination, auth tokens).
 *
 * ヘッダーとボディの両方を返すfetchApiの拡張版です。
 * レスポンスヘッダーへのアクセスが必要な場合（例：ページネーション、認証トークン）に便利です。
 *
 * @param url - Request URL (full URL format) / リクエストURL（完全なURL形式）
 * @param options - Standard fetch options (method, headers, body, etc.) / 標準のfetchオプション（method、headers、bodyなど）
 * @param schema - Zod schema for validating response body (optional) / レスポンスボディを検証するためのZodスキーマ（オプション）
 *
 * @returns
 * - If schema is provided: Object with headers and parsed body / スキーマが提供された場合：ヘッダーとパースされたボディを含むオブジェクト
 * - If schema is not provided: Object with headers only / スキーマが提供されない場合：ヘッダーのみを含むオブジェクト
 *
 * @throws {Response} Throws Response error with status code for HTTP errors (4xx, 5xx)
 *                    HTTPエラー（4xx、5xx）の場合、ステータスコード付きのResponseエラーをスロー
 *
 * @example
 * ```typescript
 * // Get headers and parsed body / ヘッダーとパースされたボディを取得
 * const { headers, body } = await fetchApiResponse('/api/users/123', { headers }, UserSchema);
 * const totalCount = headers.get('X-Total-Count');
 *
 * // Get headers only / ヘッダーのみを取得
 * const { headers } = await fetchApiResponse('/api/logout', { method: 'POST', headers });
 * const newToken = headers.get('X-Auth-Token');
 * ```
 */
export async function fetchApiResponse<Res>(
  url: string,
  options: RequestInit | undefined,
  schema: ZodType<Res>
): Promise<ApiResponse<Res>>;
export async function fetchApiResponse(url: string, options?: RequestInit): Promise<ApiResponse<void>>;

// Implementation of fetchApiResponse
export async function fetchApiResponse<Res>(
  url: string,
  options?: RequestInit,
  schema?: ZodType<Res>
): Promise<ApiResponse<Res | void>> {
  const startTime = performance.now();

  // Create deep copy of headers in DEBUG mode (to avoid WeakMap caching issues)
  let debugHeaders: Record<string, string> | undefined;
  if (DEBUG_LEVEL > 0 && options?.headers) {
    debugHeaders = {};
    if (options.headers instanceof Headers) {
      options.headers.forEach((value, key) => {
        debugHeaders![key] = value;
      });
    } else if (Array.isArray(options.headers)) {
      options.headers.forEach(([key, value]) => {
        debugHeaders![key] = value;
      });
    } else {
      debugHeaders = { ...options.headers };
    }
  }
  let rawRequestBody = undefined;
  if (DEBUG_LEVEL > 1 && options && options.body) rawRequestBody = JSON.parse(options.body as string);

  const res = await fetch(url, options);
  let debugResponseHeaders: Record<string, string> | undefined;
  if (DEBUG_LEVEL > 0) {
    debugResponseHeaders = {};
    res.headers.forEach((value, key) => {
      debugResponseHeaders![key] = value;
    });
  }

  if (!res.ok) {
    const errorBody = await res.text();
    const duration = performance.now() - startTime;

    logApiRequest({
      url,
      method: options?.method,
      headers: debugHeaders,
      body: rawRequestBody,
      status: res.status,
      duration,
      responseHeaders: debugResponseHeaders,
      error: `${res.statusText}: ${errorBody}`,
    });

    // Check for session invalid error (400 with code "4000")
    // セッション無効エラーをチェック（400 with code "4000"）
    if (res.status === 400) {
      // Only attempt JSON parsing if Content-Type indicates JSON
      // Content-TypeがJSONを示す場合のみJSONパースを試行
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        let errorJson;
        try {
          errorJson = JSON.parse(errorBody);
        } catch (parseError) {
          // Log detailed error information for debugging
          // デバッグのため詳細なエラー情報をログに記録
          logger.error(
            {
              parseError,
              errorBody,
              url,
              originalStatus: res.status,
              contentType,
            },
            'Failed to parse error response as JSON'
          );

          // Throw 502 Bad Gateway when server returns invalid JSON
          // サーバーが無効なJSONを返した場合は502 Bad Gatewayをthrow
          throw new Response('Invalid JSON response from server', {
            status: 502,
            statusText: 'Bad Gateway',
            headers: {
              'Content-Type': 'text/plain',
            },
          });
        }

        if (errorJson.code === API_ERROR_CODES.SESSION_INVALID) {
          const detailedError = {
            type: 'session_invalid',
            message: 'Session ID is invalid or expired',
            url,
          };
          logger.error(detailedError, 'Session invalid error occurred');

          // Throw Response with 401 status for proper ErrorBoundary handling
          // ErrorBoundaryで適切に処理するため401ステータスのResponseをthrow
          throw new Response(null, {
            status: 401,
            statusText: 'Unauthorized',
          });
        }
      }
    }

    // Copy API response headers to the thrown Response because we need to access
    // the x-err-code header when the API returns an error.
    // APIがエラーを返した際にx-err-codeヘッダーを参照する必要が生まれたため、
    // APIのレスポンスヘッダーをここでthrowするResponseにコピーしている。
    const errHeaders = new Headers(res.headers);
    // Fix: Since the error body is 'api error' (plain text) but the headers are copied
    // from the original response (which may have content-type: application/json),
    // we must explicitly override the content-type to text/plain to prevent parsing errors.
    // 修正: エラーボディは'api error'（プレーンテキスト）だが、ヘッダーは元のレスポンス
    // （content-type: application/jsonの可能性がある）からコピーされるため、
    // パースエラーを防ぐためcontent-typeを明示的にtext/plainで上書きする必要がある
    errHeaders.set('content-type', 'text/plain');
    throw new Response('api error', { status: res.status, statusText: res.statusText, headers: errHeaders });
  }

  // Parse JSON if schema is provided, skip otherwise
  let data: any;
  if (schema) {
    data = await res.json();
  }

  const duration = performance.now() - startTime;

  logApiRequest({
    url,
    method: options?.method,
    headers: debugHeaders,
    body: rawRequestBody,
    status: res.status,
    duration,
    responseHeaders: debugResponseHeaders,
    responseBody: schema ? data : undefined,
  });

  // Return object with headers and parsed body
  if (schema) {
    const parsedData = await schema.parseAsync(data);
    return { headers: res.headers, body: parsedData };
  } else {
    return { headers: res.headers, body: undefined as any };
  }
}

/**
 * Unified fetch function with standard fetch API argument order
 * 標準のfetch APIの引数順序を持つ統合されたfetch関数
 *
 * @description
 * Core function that performs HTTP requests to API endpoints and processes responses.
 * Provides type-safe validation with Zod schemas, automatic error handling,
 * and debug logging capabilities.
 *
 * APIエンドポイントへのHTTPリクエストを実行し、レスポンスを処理するコア関数です。
 * Zodスキーマによる型安全な検証、自動エラーハンドリング、
 * デバッグロギング機能を提供します。
 *
 * @param url - Request URL (full URL format) / リクエストURL（完全なURL形式）
 * @param options - Standard fetch options (method, headers, body, etc.) / 標準のfetchオプション（method、headers、bodyなど）
 * @param schema - Zod schema for validating response body (optional) / レスポンスボディを検証するためのZodスキーマ（オプション）
 *
 * @returns
 * - If schema is provided: Parsed response data / スキーマが提供された場合：パースされたレスポンスデータ
 * - If schema is not provided: void (response body is not read) / スキーマが提供されない場合：void（レスポンスボディは読み込まれません）
 *
 * @throws {Response} Throws Response error with status code for HTTP errors (4xx, 5xx)
 *                    HTTPエラー（4xx、5xx）の場合、ステータスコード付きのResponseエラーをスロー
 *
 * @example
 * ```typescript
 * // Parse JSON response / JSONレスポンスをパース
 * const user = await fetchApi('/api/users/123', { headers }, UserSchema);
 *
 * // No response body (e.g., logout) / レスポンスボディなし（例：ログアウト）
 * await fetchApi('/api/logout', { method: 'POST', headers });
 * ```
 *
 * @remarks
 * - Automatically reads response body as text and logs it for HTTP errors
 *   HTTPエラーの場合、自動的にレスポンスボディをテキストとして読み取り、ログに記録
 * - Logs request/response details when DEBUG_LEVEL environment variable is set
 *   DEBUG_LEVEL環境変数が設定されている場合、リクエスト/レスポンスの詳細をログに記録
 * - Creates deep copy of headers to avoid WeakMap caching issues
 *   WeakMapのキャッシュ問題を回避するため、ヘッダーのディープコピーを作成
 */
export async function fetchApi<Res>(url: string, options: RequestInit | undefined, schema: ZodType<Res>): Promise<Res>;
export async function fetchApi(url: string, options?: RequestInit): Promise<void>;

// Unified implementation - now uses fetchApiResponse internally
export async function fetchApi<Res>(url: string, options?: RequestInit, schema?: ZodType<Res>): Promise<Res | void> {
  if (schema) {
    const { body } = await fetchApiResponse(url, options, schema);
    return body;
  } else {
    await fetchApiResponse(url, options);
  }
}
